import React, { useState, useEffect } from "react";
import Layout from "../Shared/Layout";
import { toast } from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
    cartCheckout,
    decrQuantity,
    deleteItem,
    get_cart_Data,
    incrQuantity,
} from "../../redux/action";
import "./cart.css";
import Summary from "./Summary";
import { Toaster } from "react-hot-toast";
import Breadcrumb from "../Breadcrumb";
import { formatCurrencyAmount, getCurrencySymbol } from "../../utils/currency";
import { useWishlist } from "../../context/wishlistContext";

const Cart = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { ipAddress } = useWishlist();

    const reduxCartData = useSelector((state) => state.cartReducer.cart) || [];

    const [cartList, setCartList] = useState([]);
    const [buyFormData, setBuyFormData] = useState([]);
    const [loadingId, setLoadingId] = useState(null);

    useEffect(() => {
        setCartList(Array.isArray(reduxCartData) ? reduxCartData : []);
    }, [reduxCartData]);

    useEffect(() => {
        const cartItems = cartList.map((item) => {
            const qty = Number(item?.quantity || 1);
            const price = Number(item?.unitPrice || item?.price || 0);

            return {
                skuId: item?.skuId || 0,
                cartItemId: item?.cartItemId || item?.id || 0,
                productId: item?.productId || item?.product_id || item?.product?.id || 0,
                isLoggedIn: Number(localStorage.getItem("isLoggedIn") || 0),
                buyStatus: Number(item?.buyStatus ?? 1),
                quantity: qty,
                unitPrice: price,
                currency_symbol: getCurrencySymbol(item),
                currencySymbol: getCurrencySymbol(item),
                currencyCode: item?.currency_code || item?.currencyCode || item?.product?.currency_code || "",
                currency_code: item?.currency_code || item?.currencyCode || item?.product?.currency_code || "",
                discount: Number(item?.discount || item?.product?.discount || 0),
                shippingCharge: Number(
                    item?.shippingCharge ??
                    item?.shipping_cost ??
                    item?.shippingCost ??
                    item?.product?.shipping_cost ??
                    0
                ),
                totalAmount: price * qty,
                taxId: item?.taxId || 0,
                taxAmount: item?.taxAmount || item?.tax || 0,
            };
        });

        setBuyFormData(cartItems);
    }, [cartList]);

    useEffect(() => {
        if (!ipAddress) return;
        dispatch(get_cart_Data(ipAddress));
    }, [dispatch, ipAddress]);

    const handleCheckboxChange = (cartItemId) => {
        setBuyFormData((prev) =>
            prev.map((data) =>
                Number(data.cartItemId) === Number(cartItemId)
                    ? {
                          ...data,
                          buyStatus: data.buyStatus === 1 ? 0 : 1,
                          totalAmount:
                              data.buyStatus === 1
                                  ? 0
                                  : data.unitPrice * data.quantity,
                      }
                    : data
            )
        );
    };

    const handleIncrementQuantity = async (item) => {
        const cartItemId = item?.cartItemId || item?.id;

        if (loadingId === cartItemId) return;

        setLoadingId(cartItemId);

        try {
            await dispatch(
                incrQuantity({
                    ...item,
                    cart_id: item?.id,
                    cartItemId: item?.id,
                    product_id: item?.product_id,
                    productId: item?.product_id,
                    quantity: Number(item?.quantity || 1) + 1,
                    size: item?.size,
                    shape: item?.shape,
                    weight: item?.weight,
                    choice_1: item?.size,
                    choice_2: item?.shape,
                    choice_3: item?.weight,
                    guest_id: localStorage.getItem("guest_id"),
                    ip_address: ipAddress,
                })
            );

            await dispatch(get_cart_Data(ipAddress));
        } catch (error) {
            toast.error(error?.message || "Quantity update failed.");
        } finally {
            setLoadingId(null);
        }
    };

    const handleDecrementQuantity = async (item) => {
        const cartItemId = item?.cartItemId || item?.id;

        if (loadingId === cartItemId) return;

        if (Number(item?.quantity || 1) <= 1) {
            toast.error("Minimum quantity is 1");
            return;
        }

        setLoadingId(cartItemId);

        try {
            await dispatch(
                decrQuantity({
                    ...item,
                    cart_id: item?.id,
                    cartItemId: item?.id,
                    product_id: item?.product_id,
                    productId: item?.product_id,
                    quantity: Number(item?.quantity || 1) - 1,
                    size: item?.size,
                    shape: item?.shape,
                    weight: item?.weight,
                    choice_1: item?.size,
                    choice_2: item?.shape,
                    choice_3: item?.weight,
                    guest_id: localStorage.getItem("guest_id"),
                    ip_address: ipAddress,
                })
            );

            await dispatch(get_cart_Data(ipAddress));
        } catch (error) {
            toast.error(error?.message || "Quantity update failed.");
        } finally {
            setLoadingId(null);
        }
    };

    const handleDeleteItem = async (item) => {
        const isConfirmed = window.confirm(
            "Are you sure you want to delete this item?"
        );

        if (!isConfirmed) return;

        try {
            await dispatch(
                deleteItem({
                    ...item,
                    cart_id: item?.id,
                    cartItemId: item?.id,
                    product_id: item?.product_id,
                    productId: item?.product_id,
                    ip_address: ipAddress,
                })
            );

            await dispatch(get_cart_Data(ipAddress));
        } catch (error) {
            toast.error("Error occurred while removing item.");
        }
    };

    const filteredData = Array.isArray(cartList)
        ? cartList.filter((row) => row?.quantity != null && Number(row.quantity) > 0)
        : [];
    const cartCurrencySymbol = getCurrencySymbol(filteredData?.[0] || cartList?.[0]);
    const selectedCartItems = buyFormData.filter((item) => Number(item.buyStatus) === 1);
    const subtotalAmount = selectedCartItems
        .map((item) => Number(item.unitPrice || 0) * Number(item.quantity || 1))
        .reduce((preVal, curVal) => preVal + curVal, 0);
    const shippingAmount = Number(
        selectedCartItems.find((item) => Number(item.shippingCharge || 0) > 0)?.shippingCharge || 0
    );
    const totalAmount = subtotalAmount + shippingAmount;
    const formattedSubtotal = subtotalAmount.toFixed(2);
    const formattedShipping = shippingAmount.toFixed(2);
    const formattedTotal = totalAmount.toFixed(2);

    const onProccedPay = async () => {
        if (parseFloat(formattedTotal) === 0) {
            alert("No items selected! Please add items to the cart before proceeding.");
            navigate("/products");
            return;
        }

        await dispatch(cartCheckout(buyFormData));
        localStorage.removeItem("buyNowCartKey");
        navigate("/checkout");
        localStorage.removeItem("itemDetail");
    };

    const onProductDetail = (item) => {
        if (!item) return;

        localStorage.removeItem("sizeTypeName");
        localStorage.removeItem("colorTypeName");
        localStorage.removeItem("skuId");
        localStorage.removeItem("colorId");
        localStorage.removeItem("weightTypeName");
        localStorage.removeItem("shapeTypeName");

        const product = item?.product || {};
        const productSlug = item?.slug || product?.slug;

        if (!productSlug) return;

        localStorage.setItem("productId", item?.product_id || product?.id || "");
        localStorage.setItem("productCategoryId", product?.category_id || "");
        localStorage.setItem("productSubCategoryId", product?.sub_category_id || "");

        const productUrl = `/product/${productSlug}`;

        navigate(productUrl);
        localStorage.setItem("priviousUrl", productUrl);
    };

    return (
        <Layout showBreadcrumb={false}>
            <Toaster position="top-right" />
            <section className="contact-banner-sec">
                <div className="container-fluid">
                    <div className="page-header">
                        <h1>Cart</h1>
                    </div>
                    <Breadcrumb />
                </div>
            </section>
            <section className="same_space">
                <div className="container">
                    <div className="row cart justify-content-between">
                        <div className="cart-content col-xl-8 col-md-7 col-12 pb-4">
                            {filteredData.length > 0 ? (
                                filteredData.map((item, i) => {
                                    const cartItemId = item?.cartItemId || item?.id;

                                    const buyData = buyFormData.find(
                                        (data) =>
                                            Number(data.cartItemId) === Number(cartItemId)
                                    );

                                    const product = item?.product || {};

                                    const imagePath =
                                        product?.thumbnail_full_url ||
                                        product?.images_full_url?.[0]?.path ||
                                        (item?.thumbnail?.startsWith?.("http")
                                            ? item.thumbnail
                                            : item?.thumbnail
                                            ? `https://mugemsapi.lifecode.co.in/storage/app/public/product/thumbnail/${item?.thumbnail}`
                                            : "./images/defaultImage.png");

                                    const productName =
                                        item?.displayName ||
                                        item?.name ||
                                        product?.name ||
                                        "Product";

                                    const productDesc =
                                        item?.shortDesc ||
                                        product?.details?.replace(/<[^>]*>/g, "") ||
                                        "";

                                    const productSize =
                                        item?.sizeTypeName ||
                                        item?.size ||
                                        item?.choices?.Size ||
                                        "-";

                                    const productShape =
                                        item?.shapeTypeName ||
                                        item?.shape ||
                                        item?.choices?.Shape ||
                                        "-";

                                    const productWeight =
                                        item?.weightTypeName ||
                                        item?.weight ||
                                        item?.choices?.Weight ||
                                        "-";

                                    const productPrice = Number(
                                        item?.unitPrice || item?.price || 0
                                    );

                                    const isItemLoading =
                                        Number(loadingId) === Number(cartItemId);

                                    return (
                                        <div key={cartItemId || i}>
                                            <div className="cart-detail mb-sm-auto mb-0">
                                                <div className="row p-sm-3 p-0">
                                                    <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-4 col-sm-3 col-12">
                                                        <div className="select_product_check_wrapper d-sm-flex align-items-center">
                                                            <div className="check_box_wrapper">
                                                                <span>
                                                                    <input
                                                                        name="buyStatus"
                                                                        type="checkbox"
                                                                        checked={
                                                                            Number(
                                                                                buyData?.buyStatus ??
                                                                                    1
                                                                            ) === 1
                                                                        }
                                                                        onChange={() =>
                                                                            handleCheckboxChange(cartItemId)
                                                                        }
                                                                    />
                                                                </span>
                                                            </div>

                                                            <div className="p-image ">
                                                                <img
                                                                    onClick={() => onProductDetail(item)}
                                                                    style={{ cursor: "pointer" }}
                                                                    src={imagePath}
                                                                    alt={productName}
                                                                    onError={(event) => {
                                                                        event.currentTarget.src = "./images/defaultImage.png";
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="col-xxl-9 col-xl-9 col-lg-9 col-md-8 col-sm-9 col-12">
                                                        <div className="product-cart-detail">
                                                            <h5
                                                                className="mb-md-auto curser-pointer"
                                                                onClick={() => onProductDetail(item)}
                                                            >
                                                                {productName}
                                                            </h5>

                                                            <p className="short_description cart_description mb-2">
                                                                {productDesc}
                                                            </p>

                                                            <div className="cart_sku_select_data d-sm-flex align-items-center gap-3 mb-3">
                                                                <div className="cart_size">
                                                                    <span className="sku_head">
                                                                        Size :{" "}
                                                                    </span>
                                                                    <span className="sku_desc">
                                                                        {productSize}
                                                                    </span>
                                                                </div>

                                                                <div className="cart_shape">
                                                                    <span className="sku_head">
                                                                        Shape :{" "}
                                                                    </span>
                                                                    <span className="sku_desc">
                                                                        {productShape}
                                                                    </span>
                                                                </div>

                                                                <div className="cart_weight">
                                                                    <span className="sku_head">
                                                                        Weight :{" "}
                                                                    </span>
                                                                    <span className="sku_desc">
                                                                        {productWeight}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            <div
                                                                className="d-sm-flex d-md-block d-lg-flex align-items-center count_weepr"
                                                                style={{
                                                                    color: "#D0D0D0",
                                                                    fontWeight: "600",
                                                                    gap: "20px",
                                                                }}
                                                            >
                                                                <div
                                                                    className="d-flex justify-content-between align-items-center mb-lg-auto mb-md-2 mb-sm-auto mb-3"
                                                                    style={{ gap: "20px" }}
                                                                >
                                                                    <div className="d-flex cart-plus-minus">
                                                                        <span
                                                                            className="plus_counter"
                                                                            onClick={() =>
                                                                                !isItemLoading &&
                                                                                handleDecrementQuantity(item)
                                                                            }
                                                                            style={{
                                                                                cursor: "pointer",
                                                                                opacity: isItemLoading ? 0.5 : 1,
                                                                            }}
                                                                        >
                                                                            -
                                                                        </span>

                                                                        <span>{item?.quantity}</span>

                                                                        <span
                                                                            className="minus_counter"
                                                                            onClick={() =>
                                                                                !isItemLoading &&
                                                                                handleIncrementQuantity(item)
                                                                            }
                                                                            style={{
                                                                                cursor: "pointer",
                                                                                opacity: isItemLoading ? 0.5 : 1,
                                                                            }}
                                                                        >
                                                                            +
                                                                        </span>
                                                                    </div>

                                                                    <span style={{ fontSize: "20px" }}>
                                                                        {formatCurrencyAmount(productPrice, item)}
                                                                    </span>
                                                                </div>

                                                                <div className="cart-buttons remove_btn">
                                                                    <div
                                                                        className="clear"
                                                                        onClick={() => handleDeleteItem(item)}
                                                                    >
                                                                        <button className="web_buttons">
                                                                            Remove Item
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            ) : (
                                <div className="text-white py-3">Cart is Empty</div>
                            )}

                            <div className="cart-buttons mt-sm-3 mt-3">
                                <div className="continue">
                                    <Link to="/">
                                        <p className="web_buttons">Continue Shopping</p>
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {filteredData.length > 0 && parseFloat(formattedTotal) > 0 ? (
                            <div className="pcard d-flex justify-content-center col-xl-4 col-md-5 col-12 mb-4">
                                <Summary
                                    onProccedPay={onProccedPay}
                                    formattedSubtotal={formattedSubtotal}
                                    formattedShipping={formattedShipping}
                                    formattedTotal={formattedTotal}
                                    shippingAmount={shippingAmount}
                                    currencySymbol={cartCurrencySymbol}
                                />
                            </div>
                        ) : null}
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default Cart;
